/**
 * Application Detail Modal Component
 * Modal for viewing application details
 */

import React from 'react';
import { APPLICATION_STATUS, SCREENING_FORMATS } from '../../config/constants';
import './ApplicationDetailModal.css';

const ApplicationDetailModal = ({
  isOpen,
  onClose,
  application,
  onReview,
  canApproveApplications
}) => {
  if (!isOpen || !application) return null;


  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case APPLICATION_STATUS.PENDING:
        return '#ffc107';
      case APPLICATION_STATUS.APPROVED:
        return '#28a745';
      case APPLICATION_STATUS.REJECTED:
        return '#dc3545';
      case APPLICATION_STATUS.SUSPENDED:
        return '#6c757d';
      case APPLICATION_STATUS.TERMINATED:
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getScreeningFormatLabel = (format) => {
    return Object.entries(SCREENING_FORMATS).find(([, value]) => value === format)?.[0] || format;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Application Details</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Application Status */}
          <div className="detail-section">
            <div className="section-header">
              <h4>Application Status</h4>
              <span
                className="status-badge"
                style={{ backgroundColor: getStatusColor(application.status) }}
              >
                {application.status_display || application.status}
              </span>
            </div>
            <div className="status-info">
              <div className="info-item">
                <span className="info-label">Member ID:</span>
                <span className="info-value">{application.member_id || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created:</span>
                <span className="info-value">{formatDateTime(application.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Updated:</span>
                <span className="info-value">{formatDateTime(application.updated_at)}</span>
              </div>
            </div>
          </div>

          {/* Organization Information */}
          <div className="detail-section">
            <h4>Organization Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Organization Name:</span>
                <span className="info-value">{application.organization_name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Screening Frequency:</span>
                <span className="info-value">{application.screening_frequency || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Screening Formats:</span>
                <span className="info-value">
                  {application.screening_format && application.screening_format.length > 0
                    ? application.screening_format.map(format => getScreeningFormatLabel(format)).join(', ')
                    : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Executive Committee Information */}
          <div className="detail-section">
            <h4>Executive Committee Information</h4>

            {/* President Information */}
            <div className="committee-member">
              <h5>President</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{application.president_name || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">
                    {application.president_email ? (
                      <a href={`mailto:${application.president_email}`}>
                        {application.president_email}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">
                    {application.president_phone ? (
                      <a href={`tel:${application.president_phone}`}>
                        {application.president_phone}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Secretary Information */}
            <div className="committee-member">
              <h5>Secretary</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{application.secretary_name || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">
                    {application.secretary_email ? (
                      <a href={`mailto:${application.secretary_email}`}>
                        {application.secretary_email}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">
                    {application.secretary_phone ? (
                      <a href={`tel:${application.secretary_phone}`}>
                        {application.secretary_phone}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Treasurer Information */}
            <div className="committee-member">
              <h5>Treasurer</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{application.treasurer_name || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">
                    {application.treasurer_email ? (
                      <a href={`mailto:${application.treasurer_email}`}>
                        {application.treasurer_email}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">
                    {application.treasurer_phone ? (
                      <a href={`tel:${application.treasurer_phone}`}>
                        {application.treasurer_phone}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Preferences */}
            <div className="contact-preferences">
              <h5>Contact Preferences</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Primary Contact:</span>
                  <span className="info-value">
                    {application.primary_contact_name || 'N/A'}
                    {application.contact_person_display ? ` (${application.contact_person_display})` : ''}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Applicant Role:</span>
                  <span className="info-value">{application.applicant_role_display || application.applicant_role || 'N/A'}</span>
                </div>
                {(application.contact_person === 'other' || application.applicant_role === 'other') && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Other Contact Name:</span>
                      <span className="info-value">{application.other_name || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Other Role:</span>
                      <span className="info-value">{application.other_role || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Other Email:</span>
                      <span className="info-value">{application.other_email ? (<a href={`mailto:${application.other_email}`}>{application.other_email}</a>) : 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Other Phone:</span>
                      <span className="info-value">{application.other_phone ? (<a href={`tel:${application.other_phone}`}>{application.other_phone}</a>) : 'N/A'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Screening Information */}
          <div className="detail-section">
            <h4>Screening Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Screening Frequency:</span>
                <span className="info-value">{application.screening_frequency || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Screening Venue:</span>
                <span className="info-value">{application.screening_venue || 'N/A'}</span>
              </div>
              <div className="info-item full-width">
                <span className="info-label">Screening Formats:</span>
                <span className="info-value">
                  {application.screening_format && application.screening_format.length > 0
                    ? application.screening_format.map(format => getScreeningFormatLabel(format)).join(', ')
                    : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="detail-section">
            <h4>Address Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Street Address:</span>
                <span className="info-value">{application.street_address_1}</span>
              </div>
              {application.street_address_2 && (
                <div className="info-item">
                  <span className="info-label">Street Address 2:</span>
                  <span className="info-value">{application.street_address_2}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">City:</span>
                <span className="info-value">{application.city}</span>
              </div>
              <div className="info-item">
                <span className="info-label">State:</span>
                <span className="info-value">{application.state}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Postcode:</span>
                <span className="info-value">{application.postcode}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Country:</span>
                <span className="info-value">{application.country}</span>
              </div>
            </div>
          </div>

          {/* Application Settings & Permissions */}
          <div className="detail-section">
            <h4>Application Settings & Permissions</h4>

            {/* Application Type */}
            <div className="settings-group">
              <h5>Application Type</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Renewal Application:</span>
                  <span className="info-value">
                    <span className={`status-indicator ${application.renewal ? 'enabled' : 'disabled'}`}>
                      {application.renewal ? 'Yes' : 'No'}
                    </span>
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Current Members Count:</span>
                  <span className="info-value">{application.current_members_count || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Data Sharing Preferences */}
            <div className="settings-group">
              <h5>Data Sharing Preferences</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Release to Third Parties:</span>
                  <span className="info-value">
                    <span className={`status-indicator ${application.release_to_third_parties ? 'enabled' : 'disabled'}`}>
                      {application.release_to_third_parties ? 'Yes' : 'No'}
                    </span>
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Publish on Website:</span>
                  <span className="info-value">
                    <span className={`status-indicator ${application.publish_on_website ? 'enabled' : 'disabled'}`}>
                      {application.publish_on_website ? 'Yes' : 'No'}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="settings-group">
              <h5>Terms and Conditions</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Agreed to Conditions:</span>
                  <span className="info-value">
                    <span className={`status-indicator ${application.agreed_to_conditions ? 'enabled' : 'disabled'}`}>
                      {application.agreed_to_conditions ? 'Yes' : 'No'}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            {application.notes && (
              <div className="info-item full-width">
                <span className="info-label">Notes:</span>
                <span className="info-value">{application.notes}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
          {canApproveApplications && application.status === APPLICATION_STATUS.PENDING && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => onReview(application.id, { status: APPLICATION_STATUS.APPROVED })}
              >
                Approve
              </button>
              <button
                className="btn btn-danger"
                onClick={() => onReview(application.id, { status: APPLICATION_STATUS.REJECTED })}
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailModal;
